import { svgIconClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Radio, { radioClasses } from '@mui/material/Radio';
import { SxProps } from '@mui/system';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersDispatch,
  GetV1CompaniesCompanyIdOrdersDispatchData,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LoadingSpinner,
  OverflowAwareText,
} from '~components/Order/ordersDispatchStyledComponents';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { Order } from '~hooks/useOrders/models';
import { extractPagination, Pagination } from '~services/pagination';
import useInfiniteScroll from '~utils/hooks/useInfiniteScroll';

import CustomerSelector from './copy-vendor-assignments-form/CustomerSelector';
import DateRangeSelector from './copy-vendor-assignments-form/DateRangeSelector';
import SearchBar from './copy-vendor-assignments-form/SearchBar';

interface CopyVendorAssignmentsFormProps {
  companyId: string;
  onSelectedOrderChange: (order: Order) => void;
  orderId?: string;
}

type Filters = {
  customerAccountIds: string[];
  endDate: string;
  search: string;
  startDate: string;
};

type QueryParamsState = {
  filters: Filters;
  pagination?: Pagination;
};

const deriveQueryFromQueryParams = (queryParams: QueryParamsState) => {
  const { filters, pagination } = queryParams;

  const query: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = { 'page[limit]': 20 };

  if (pagination?.after) {
    query['page[after]'] = pagination.after;
  }

  if (filters?.search?.trim().length) {
    query['search[job][datagrid]'] = filters.search.trim();
  }

  if (filters?.startDate?.length) {
    query['filter[job][start_date]'] = dayjs
      .tz(filters.startDate)
      .startOf('day')
      .toISOString();
  }

  if (filters?.endDate?.length) {
    query['filter[job][end_date]'] = dayjs.tz(filters.endDate).endOf('day').toISOString();
  }

  if (filters?.customerAccountIds?.length) {
    query['filter[job][customer_account_ids]'] = filters.customerAccountIds;
  }

  return query;
};

export default function CopyVendorAssignmentsForm({
  companyId,
  onSelectedOrderChange,
  orderId,
}: CopyVendorAssignmentsFormProps) {
  const theme = useTheme();
  const infiniteScroll = useInfiniteScroll();

  const [queryParams, setQueryParams] = useState<QueryParamsState>({
    filters: {
      customerAccountIds: [],
      endDate: '',
      search: '',
      startDate: '',
    },
  });

  const { isLoading, isFetchingNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: [
      'CopyVendorAssignmentsForm_getV1CompaniesCompanyIdOrdersDispatch',
      queryParams.filters,
      companyId,
    ],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: ({ pageParam, signal }) => {
      const query = deriveQueryFromQueryParams({ ...queryParams, pagination: pageParam });

      return getV1CompaniesCompanyIdOrdersDispatch({
        query,
        path: { 'company-id': companyId },
        signal,
      }).then((response) => {
        const orders = response.data.data.map((order) => Order.parse(order));
        const pagination = extractPagination(response);

        setQueryParams((state) => ({ ...state, pagination }));
        infiniteScroll.notify();

        return orders;
      });
    },
  });

  const orders = useMemo(() => (data?.pages ?? []).flat(), [data?.pages]);

  const handleDateRangeChange = useCallback((startDate?: string, endDate?: string) => {
    setQueryParams((state) => ({
      filters: { ...state.filters, startDate: startDate ?? '', endDate: endDate ?? '' },
    }));
  }, []);

  const handleCustomerAccountsChange = useCallback((customerAccountIds: string[]) => {
    setQueryParams((state) => ({
      filters: { ...state.filters, customerAccountIds },
    }));
  }, []);

  const handleSearchValueChange = useCallback((search: string) => {
    setQueryParams((state) => ({ filters: { ...state.filters, search } }));
  }, []);

  useEffect(() => {
    if (infiniteScroll.endReached) {
      fetchNextPage();
    }
  }, [infiniteScroll.endReached, fetchNextPage]);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          bgcolor: theme.semantic.surfaceRaised,
          borderBottom: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          flex: 0,
          gap: 1,
          p: theme.spacing(1, 2),
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <DateRangeSelector onChange={handleDateRangeChange} />

        <CustomerSelector companyId={companyId} onChange={handleCustomerAccountsChange} />

        <SearchBar onChange={handleSearchValueChange} />
      </Box>

      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        {isLoading ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <LoadingSpinner isVisible />
          </Box>
        ) : (
          <>
            <OrdersTable
              onSelectedOrderChange={onSelectedOrderChange}
              orders={orders}
              selectedOrderId={orderId}
            />

            {orders.length > 0 && queryParams.pagination?.after && (
              <Box ref={infiniteScroll.nodeRef}>
                <LoadingSpinner
                  isVisible={isFetchingNextPage}
                  loadingIndicatorSize={16}
                  sx={{ my: 2 }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

interface OrdersTableProps {
  onSelectedOrderChange: (order: Order) => void;
  orders: Order[];
  selectedOrderId?: string;
}

function OrdersTable({
  onSelectedOrderChange,
  orders,
  selectedOrderId,
}: OrdersTableProps) {
  const { singular } = useDispatchNumberLabel();

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'grid',
        gridAutoRows: '32px',
        gridTemplateColumns: 'auto repeat(10, 1fr)',
      }}
    >
      <TableHeader label="" />
      <TableHeader label={singular('order.form.dispatch_number_full')} />
      <TableHeader label={t('common.date')} />
      <TableHeader label={t('common.project_name')} />
      <TableHeader label={t('common.project_ext_id')} />
      <TableHeader label={t('form_fields.foreman')} />
      <TableHeader label={t('common.customer')} />
      <TableHeader label={t('form_fields.pick_up')} />
      <TableHeader label={t('form_fields.material')} />
      <TableHeader
        label={t('dispatch.dispatch_v2.copy_vendor_assignments.tread_order_id')}
      />
      <TableHeader label={t('order.form.order_name')} />

      {orders.map((order, index) => (
        <React.Fragment key={order.id}>
          <TableCell isFirstRow={index === 0}>
            <RadioButton
              isChecked={selectedOrderId === order.id}
              onCheckedStateChange={() => onSelectedOrderChange(order)}
              orderId={order.orderId}
            />
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.dispatchNumber?.name}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>
              {dayjs.tz(order.loadAt).format('MM/DD/YYYY')}
            </OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.projectName}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.project?.externalId}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.foremen?.[0]?.fullName}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>
              {order.sendingAccounts.map((i) => i.name).join(', ')}
            </OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>
              {order.pickUpWayPoints?.[0]?.siteNested?.address?.streetAddress}
            </OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.material?.name}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.orderId}</OverflowAwareText>
          </TableCell>

          <TableCell isFirstRow={index === 0}>
            <OverflowAwareText>{order.name}</OverflowAwareText>
          </TableCell>
        </React.Fragment>
      ))}
    </Box>
  );
}

interface RadioButtonProps {
  isChecked?: boolean;
  onCheckedStateChange?: () => void;
  orderId?: string;
}

function RadioButton({ isChecked, onCheckedStateChange, orderId }: RadioButtonProps) {
  return (
    <FormControl>
      <Radio
        checked={isChecked}
        onChange={onCheckedStateChange}
        sx={{
          [`&.${radioClasses.root}`]: { p: 0 },
          [`.${svgIconClasses.root}`]: { width: '18px', height: '18px' },
        }}
        data-ui={`page-dispatch__copy-vendor-assignments__order-${orderId}__select-radio`}
      />
    </FormControl>
  );
}

interface TableCell {
  isFirstRow?: boolean;
  sx?: SxProps;
}

function TableCell({ children, isFirstRow, sx }: React.PropsWithChildren<TableCell>) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        alignItems: 'center',
        borderTop: isFirstRow ? undefined : `solid 1px ${theme.palette.divider}`,
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        p: theme.spacing(0.5, 1),
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

interface TableHeaderProps {
  label: string;
}

function TableHeader({ label }: TableHeaderProps) {
  const theme = useTheme();

  return (
    <TableCell
      sx={{
        bgcolor: theme.semantic.surfaceRaised,
        borderBottom: `solid 1px ${theme.palette.divider}`,
        borderTop: 0,
        position: 'sticky',
        top: '53px',
        zIndex: 10,
      }}
    >
      <OverflowAwareText
        sx={{ textTransform: 'uppercase', color: theme.brandV2.colors.treadGray4 }}
      >
        {label}
      </OverflowAwareText>
    </TableCell>
  );
}
